import React, {useState, useEffect, useRef} from 'react';
import { createGlobalStyle, ThemeProvider } from 'styled-components';
import { styleReset } from 'react95';
import original from "react95/dist/themes/original";
import ms_sans_serif from "react95/dist/fonts/ms_sans_serif.woff2";
import ms_sans_serif_bold from "react95/dist/fonts/ms_sans_serif_bold.woff2";
import './App.css';
import DesktopIcons from './components/desktop-icons/DesktopIcons';
import { getPropertyValue } from './functions/customFunctions';
import { Helmet } from './helmet/Helmet';
import { Taskbar } from './components/taskbar/Taskbar';
import PortfolioWindow from './components/portfolio/PortfolioWindow';
import ResumeWindow from './components/resume/ResumeWindow';
import BrowserWindow from './components/browser/BrowserWindow';
import MusicWindow from './components/music/MusicWindow';
import WelcomeWindow from './components/welcome/WelcomeWindow';
import RecycleBinWindow from './components/recyclebin/RecycleBinWindow';
import WarningWindow from './components/warning/WarningWindow';
import BinWarningWindow from './components/warning/BinWarningWindow';
import RecycleWarningWindow from './components/warning/RecycleWarningWindow';
import BlueScreen from './components/bluescreen/BlueScreen';
import DoomWindow from './components/doom/DoomWindow';
import PaintWindow from './components/paint/PaintWindow';
import win95energystar from './assets/images/win95-energystar.gif';
import win95energystarMobile from './assets/images/win95-energystar-mobile.gif';
import win95startup from './assets/images/win95-startup.jpg';
import win95startupMobile from './assets/images/win95-startup-mobile.jpeg';
import win95shutdown from './assets/images/win95-shutdown.png';
import win95shutdownMobile from './assets/images/win95-shutdown-mobile.png';
import safeTurnOff from './assets/images/safe-turn-off.jpeg';
import { Mailnews20, Shell32167, MediaCd, Shell3232, Shell3233, Mspaint, Gcdef100 } from '@react95/icons'
import RecycleBinContent from './components/recyclebin/RecycleBinContent';
import win95recycle from './assets/sounds/win95recycle.wav'
import win95error from './assets/sounds/win95error.mp3'
import GitIcon from './components/GitIcon';
import DesktopAssistant from './components/DesktopAssistant';

const recycleAudio = new Audio(win95recycle);
const errorAudio = new Audio(win95error);

const GlobalStyles = createGlobalStyle`
  @font-face {
    font-family: 'ms_sans_serif';
    src: url('${ms_sans_serif}') format('woff2');
    font-weight: 400;
    font-style: normal
  }
  @font-face {
    font-family: 'ms_sans_serif';
    src: url('${ms_sans_serif_bold}') format('woff2');
    font-weight: bold;
    font-style: normal
  }
  body {
    font-family: 'ms_sans_serif';
  }
  ${styleReset}
`;

const App = () => {
  const [isTouchDevice] = useState('ontouchstart' in window || navigator.maxTouchPoints > 0 || navigator.msMaxTouchPoints > 0)
  const [startingUp, setStartingUp] = useState(true)
  const tasks = ["portfolio", "resume", "browser", "music", "recycle bin", "doom", "paint"];
  const minTaskIndex = tasks.length + 5
  const initializeTasksState = (state) => {
    const initialState = {};
    tasks.forEach(task => {
      initialState[task] = state;
    });
    return initialState;
  };
  const [tasksVisibility, setTasksVisibility] = useState(initializeTasksState('visible'));
  const [taskIndices, setTaskIndices] = useState(initializeTasksState(minTaskIndex));
  const [projectUrl, setProjectUrl] = useState(null)
  const [dockMenuActive, setDockMenuActive] = useState(false)
  const [displayTasks, setDisplayTasks] = useState(new Set())
  const [activeTask, setActiveTask] = useState('welcome')
  const [selectedIcon, setSelectedIcon] = useState(null)
  const [selectedBinIcon, setSelectedBinIcon] = useState(null)
  const [displayBSOD, setDisplayBSOD] =useState('none')
  const [warnings, setWarnings] = useState(0)
  const [loading, setLoading] = useState(true)
  const [signed, setSigned] = useState(false)
  const [signingOff, setSigningOff] = useState(false)
  const [shutDown, setShutDown] = useState(false)
  const [turnOff, setTurnOff] = useState(false)
  const [energyStar, setEnergyStar] = useState(true)
  useEffect(() => {
    if (signed) {
      setActiveTask(null);
    }
  }, [signed]);
  const [binLastPos, setBinLastPos] = useState(null)
  const [iconDragPoint, setIconDragPoint] = useState({x: 0, y: 0})
  const [taskSwitiching, setTaskSwitiching] = useState(false)
  const [icons, setIcons] = useState({
    'resume': {
      Icon: Mailnews20,
      desktopRef: useRef(null),
      iconRef: useRef(null),
      position: { x: 125, y: 25 },
    },
    'git': {
      Icon: GitIcon,
      desktopRef: useRef(null),
      iconRef: useRef(null),
      binIconRef: useRef(null),
      position: { x: 125, y: 150 },
      visibility: 'visible',
    },
    'music': {
      Icon: MediaCd,
      desktopRef: useRef(null),
      iconRef: useRef(null),
      binIconRef: useRef(null),
      position: { x: 25, y: 150 },
      visibility: 'visible',
    },
    'portfolio': {
      Icon: Shell32167,
      desktopRef: useRef(null),
      iconRef: useRef(null),
      binIconRef: useRef(null),
      position: { x: 25, y: 25 },
      visibility: 'visible',
    },
    'recycle bin': {
      Icon: Shell3232,
      desktopRef: useRef(null),
      iconRef: useRef(null),
      position: { x: 25, y: 275 },
    },
    'doom': {
      Icon: Gcdef100,
      desktopRef: useRef(null),
      iconRef: useRef(null),
      position: { x: 225, y: 25 },
      visibility: 'visible',
    },
    'paint': {
      Icon: Mspaint,
      desktopRef: useRef(null),
      iconRef: useRef(null),
      position: { x: 225, y: 150 },
      visibility: 'visible',
    },
  })
  const [iconsInBin, setIconsInBin] = useState(new Set())

  const settingIconsInBin = {
    unshifting: (task) => {
      setIconsInBin(prevIcons => {
        prevIcons.delete(task);
        const arr = Array.from(prevIcons);
        arr.unshift(task);
        return new Set(arr);
      });
    },
    inserting: (task) => {
      setIconsInBin(prevIcons => {
        prevIcons.delete(task);
        const arr = Array.from(prevIcons);
        arr.splice(1, 0, task);
        return new Set(arr);
      })
    },
    pushing: (task) => {
      setIconsInBin(prevIcons => {
        prevIcons.delete(task);
        const arr = Array.from(prevIcons);
        arr.push(task); 
        return new Set(arr);
      })
    }
  };
  const settingIconsInBinSimple = (boolean, task) => {
    setIconsInBin((prevState) => {
      const newState = new Set(prevState); 
      if (boolean) {
        newState.add(task);
      } else {
        newState.delete(task);
      }
      return newState;
    });
  }


  const binWindowRef = useRef(null)
  const emptyingBin = () => {
    if (iconsInBin.size === 0) {
      setIcons(prevTasks => ({
        ...prevTasks,
        "recycle bin": {
          ...prevTasks["recycle bin"],
          Icon: Shell3232
        }
      }));
    }
  }

  const recyclingIcon = (task) => {
    if (displayTasks.has('recycle bin')) {
      setTimeout(() => {
        setActiveTask('recycle bin')
        setSelectedBinIcon(task)
      }, 0);
    }
    displayingTask(false, task)
    settingIconsInBinSimple(true, task)
    setIcons(prevTasks => ({
      ...prevTasks,
      [task]: {
        ...prevTasks[task],
        visibility: 'hidden'
      },
      "recycle bin": {
        ...prevTasks["recycle bin"],
        Icon: Shell3233
      }
    }));
    recycleAudio.play();
  }
  const positioningIcon = (task, x, y) => {
    setIcons(prevTasks => ({
      ...prevTasks,
      [task]: {
        ...prevTasks[task],
        position: { x: x, y: y }
      },
    }));
  }
  const binIconsRef = useRef()
  const teleportingIcon = (event) => {
    if (!binWindowRef.current || tasksVisibility['recycle bin'] !== 'visible') { 
      return
    }
      const binRect = binWindowRef.current.getBoundingClientRect();
    const clientX = event.clientX || (event.changedTouches && event.changedTouches[0].clientX);
    const clientY = event.clientY || (event.changedTouches && event.changedTouches[0].clientY);

    if (!clientX || !clientY) return; 

    if (
      clientX < binRect.x || clientX > binRect.x + binRect.width ||
      clientY < binRect.y || clientY > binRect.y + binRect.height
    ) {
      const task = Object.keys(icons).find(taskKey => icons[taskKey].binIconRef?.current?.contains(event.target));

      if (task) {
        const offsetX = clientX - iconDragPoint.x;
        const offsetY = clientY - iconDragPoint.y;
        settingIconsInBinSimple(false, task)
        positioningIcon(task, offsetX, offsetY)
        setTimeout(() => {
          setIcons(prevTasks => ({
            ...prevTasks,
            [task]: {
              ...prevTasks[task],
              visibility: 'visible'
            }
          }));
        }, 0);
        setSelectedIcon(task)
      }
    }
    else if (
      clientX >= binRect?.x && clientX <= binRect?.x + binRect?.width &&
      clientY >= binRect?.y && clientY <= binRect?.y + binRect?.height
    ) {
      const task = Object.keys(icons).find(taskKey => icons[taskKey].desktopRef?.current?.contains(event.target));

      if (task === 'portfolio' || task === 'music' || task ==='git') {
        displayingTask(false, task)

        if (iconsInBin.size === 0) {
          settingIconsInBinSimple(true, task)
        } else if (binIconsRef.current) { 
          const binIconsRect = binIconsRef.current.getBoundingClientRect();
          const binGridStyle = window.getComputedStyle(binIconsRef.current);
          const gridPadding = getPropertyValue(binGridStyle, 'padding');
          const gridColumnWidth = getPropertyValue(binGridStyle, 'grid-template-columns');
          const twoEleventhWidth = gridPadding + gridColumnWidth*0.5
          const halfWidth = gridPadding*2 + gridColumnWidth*1.5
          if (iconsInBin.size === 1) {
            if (clientX <= binIconsRect.x + twoEleventhWidth ) {
              settingIconsInBin.unshifting(task)
            } else {
              settingIconsInBinSimple(true, task)
            }
          } else if (iconsInBin.size === 2) {
            if (clientX <= binIconsRect.x + twoEleventhWidth ) {
              settingIconsInBin.unshifting(task)
            } else if (clientX > binIconsRect.x + twoEleventhWidth && clientX <= binIconsRect.x + halfWidth) {
              settingIconsInBin.inserting(task)
            } else {
              settingIconsInBinSimple(true, task)
            }
          }
        } else { 
            settingIconsInBinSimple(true, task);
        }


        setIcons(prevTasks => ({
          ...prevTasks,
          [task]: {
            ...prevTasks[task],
            visibility: 'hidden'
          },
          "recycle bin": {
            ...prevTasks["recycle bin"],
            Icon: Shell3233
          }
        }));
        setSelectedBinIcon(task)
        recycleAudio.play();
        setActiveTask('recycle bin')
      }
      else if (task === 'resume') {
        setActiveTask('warning')
        indexingTasks('warning')
        displayingTask(true, 'warning')
        issuingWarning()
      }
      else if (task === 'recycle bin' && binLastPos) {
        positioningIcon(task, binLastPos.x, binLastPos.y)
        setActiveTask('recycle warning')
        displayingTask(true, 'recycle warning');
        errorAudio.play();
        indexingTasks('recycle warning')
      }
    }
  }

  const indexingTasks = (task) => {
    setTaskIndices((prevState) => {
      const newState = {...prevState}; 
      const sortedKeys = Object.keys(newState).sort((a, b) => newState[a] - newState[b]);
      const taskIndex = sortedKeys.indexOf(task);
      if (taskIndex === -1) return newState; 
      for (let i = taskIndex + 1; i < sortedKeys.length; i++) {
        if (newState[sortedKeys[i]] > minTaskIndex)
        newState[sortedKeys[i]] -= 1;
      }
      newState[task] = minTaskIndex * 2 - 1;
      return newState;
    });
  }

  const displayingTask = (boolean, task) => {
    setDisplayTasks((prevState) => {
      const newDisplayTasks = new Set(prevState);
      if (boolean) {
        newDisplayTasks.add(task);
      } else {
        newDisplayTasks.delete(task);
      }
      return newDisplayTasks;
    });
  };

  useEffect(() => {
    if (activeTask) {
      setTaskSwitiching(true)
    }
  }, [activeTask])

  const activiatingDockMenu = (boolean) => {
    setDockMenuActive(boolean)
    if (boolean) {
      setActiveTask(null)
    }
  }

  const handleDown = (event) => {
    activiatingDockMenu(false)
    if (event.target instanceof HTMLElement && event.target.className === 'desktop') {
        setActiveTask(null);
    }
    const desktopRefs = Object.values(icons).map(iconData => iconData.desktopRef); 
    if (desktopRefs.some(ref => ref.current?.contains(event.target))) {
      return;
    }
    setSelectedIcon(null)
  }

  const turningoff = (boolean) => {
    setSigned(boolean)
    setShutDown(boolean)
  }

  const issuingWarning = () => {
    const updatedWarnings = warnings + 1
    setWarnings(updatedWarnings)
    if (updatedWarnings >= 3) {
      displayingBSOD(true)
    }
  }

  const displayingBSOD = (boolean) => {
    if (boolean) {
      setDisplayBSOD('flex')
    } else {
      setDisplayBSOD('none')
    }
  }
  useEffect(() => {
    setTimeout(() => {
      setEnergyStar(false)
      setTimeout(() => {
        setLoading(false)
      }, 1200);
    }, 2000);
  }, []);

  const desktopRef = useRef(null)
  const [portfolioHeight, setPortfolioHeight] = useState(null)
  useEffect(() => {
    if (!desktopRef.current) return;
    if (activeTask === 'bin warning') {
      displayingTask(true, 'bin warning');
      indexingTasks('bin warning');
    } else if (activeTask) {
      activiatingDockMenu(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTask]);

  useEffect(() => {
    if (!desktopRef.current) return;
    if (portfolioHeight > 700) {
      desktopRef.current.style.minHeight = '850px';
    } else {
      desktopRef.current.style.minHeight = '100vh';
    }
  },[portfolioHeight])

  useEffect(() => {
    if (shutDown) {
      setTimeout(() => {
        setTurnOff(true)
      }, 3000);
    }
  },[shutDown])

  const [documentPosition, setDocumentPosition] = useState(0);
  const [touchStartY, setTouchStartY] = useState(null);
  const handleTouchStart = (event) => {
    if (event.touches && event.touches.length > 0) { 
        setTouchStartY(event.touches[0].clientY);
    }
    setDocumentPosition(document.documentElement.scrollTop);
  };
  const handleTouchMove = (event) => {
    if (event.touches && event.touches.length > 0) { 
        const touchEndY = event.touches[0].clientY;
        if (documentPosition === 0 && touchStartY !== null && touchEndY > touchStartY) {
            event.preventDefault();
        }
    }
  }
  useEffect(() => {
    window.addEventListener('touchmove', handleTouchMove, { passive: false });

    return () => {
      window.removeEventListener('touchmove', handleTouchMove)
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [touchStartY, documentPosition]); 

  if (turnOff) {
    return (
      <Helmet>
        <div className='shut-down-background'>
          <ThemeProvider theme={original}>
            <GlobalStyles />
            <img className='shut-down' src={safeTurnOff} alt="turn off"/>
          </ThemeProvider>
        </div>
      </Helmet>
    )
  }
  if (shutDown) {
    return (
      <Helmet>
        <div className='shut-down-background'>
          {
            window.innerWidth <= 800 ?
            <img className='shut-down-mobile' src={win95shutdownMobile} alt="shut down"/>
            :
            <img className='shut-down' src={win95shutdown} alt="shut down"/>

          }
        </div>
      </Helmet>
    )
  }
  if (energyStar) {
    return (
      <Helmet>
        <div className='start-up-background'>
          {
            window.innerWidth <= 800 ?
            <img className='start-up' src={win95energystarMobile} alt="start up"/>
            :
            <img className='start-up' src={win95energystar} alt="start up"/>

          }
        </div>
      </Helmet>
    )
  }
  if (loading) {
    return (
      <Helmet>
        <div className='start-up-background'>
          {
            window.innerWidth <= 800 ?
            <img className='start-up-mobile' src={win95startupMobile} alt="start up"/>
            :
            <img className='start-up' src={win95startup} alt="start up"/>

          }
        </div>
      </Helmet>
    )
  } 
  if (!signed) {
    return (
      <Helmet>
        <GlobalStyles />
        <ThemeProvider theme={original}>
          <div className="desktop" style={{height: "100vh", width: "100vw"}} onTouchStart={handleDown} onMouseDown={handleDown}>
            <WelcomeWindow setActiveTask={setActiveTask} activeTask={activeTask} setSigned={setSigned} />
          </div>
        </ThemeProvider>
      </Helmet>
    )
  }

  const windowProps = {
    displayingTask, displayTasks, setActiveTask, activeTask, indexingTasks, taskIndices, tasksVisibility, setTasksVisibility, setTaskSwitiching
  };
  return (
    <Helmet>
      <GlobalStyles />
      <ThemeProvider theme={original}>

          <div className="desktop" ref={desktopRef} onMouseDown={handleDown} onTouchStart={handleDown} onMouseUp={teleportingIcon} onTouchEnd={teleportingIcon} onTouchStartCapture={handleTouchStart}>
            <DesktopIcons {...windowProps} issuingWarning={issuingWarning} warnings={warnings} activiatingDockMenu={activiatingDockMenu} setSelectedIcon={setSelectedIcon} selectedIcon={selectedIcon} icons={icons} recyclingIcon={recyclingIcon}  positioningIcon={positioningIcon} setBinLastPos={setBinLastPos} isTouchDevice={isTouchDevice} taskSwitiching={taskSwitiching} />
            <ResumeWindow {...windowProps} isTouchDevice={isTouchDevice} />
            <PortfolioWindow {...windowProps} setProjectUrl={setProjectUrl} setPortfolioHeight={setPortfolioHeight} setTouchStartY={setTouchStartY} setDocumentPosition={setDocumentPosition} />
            <BrowserWindow {...windowProps} setProjectUrl={setProjectUrl} projectUrl={projectUrl} />
            <DoomWindow {...windowProps} />
            <PaintWindow {...windowProps} />
            <MusicWindow {...windowProps} signed={signed} signingOff={signingOff} isTouchDevice={isTouchDevice} icons={icons} />
            <RecycleBinWindow {...windowProps} icons={icons} setSelectedBinIcon={setSelectedBinIcon} selectedBinIcon={selectedBinIcon} emptyingBin={emptyingBin} binWindowRef={binWindowRef} isTouchDevice={isTouchDevice} setIconDragPoint={setIconDragPoint} iconsInBin={iconsInBin} setIconsInBin={setIconsInBin} binIconsRef={binIconsRef} settingIconsInBin={settingIconsInBin} />
            <BinWarningWindow {...windowProps} activiatingDockMenu={activiatingDockMenu}  />
            <RecycleWarningWindow {...windowProps} activiatingDockMenu={activiatingDockMenu} selectedIcon={selectedIcon}/>
            <WarningWindow {...windowProps} warnings={warnings} activiatingDockMenu={activiatingDockMenu} errorAudio={errorAudio} />

            {
            isTouchDevice ?
              <RecycleBinContent binWindowRef={binWindowRef} taskIndices={taskIndices} displayTasks={displayTasks} tasksVisibility={tasksVisibility} setActiveTask={setActiveTask} indexingTasks={indexingTasks} icons={icons} setSelectedBinIcon={setSelectedBinIcon} selectedBinIcon={selectedBinIcon} activeTask={activeTask} emptyingBin={emptyingBin} teleportingIcon={teleportingIcon} isTouchDevice={isTouchDevice} setIconDragPoint={setIconDragPoint} iconsInBin={iconsInBin} documentPosition={documentPosition} setDocumentPosition={setDocumentPosition} setIconsInBin={setIconsInBin} binIconsRef={binIconsRef} settingIconsInBin={settingIconsInBin} />
            :
              <></>
            }
            {signed && <DesktopAssistant />}
          </div>
          { warnings >= 3 ?
            <BlueScreen displayBSOD={displayBSOD} displayingBSOD={displayingBSOD} setActiveTask={setActiveTask} />
            :
            <></>
          }
          <Taskbar activiatingDockMenu={activiatingDockMenu} dockMenuActive={dockMenuActive} displayingTask={displayingTask} displayTasks={displayTasks} indexingTasks={indexingTasks} setSigned={setSigned} setSigningOff={setSigningOff} taskIndices={taskIndices} turningoff={turningoff} setTasksVisibility={setTasksVisibility} tasksVisibility={tasksVisibility} setActiveTask={setActiveTask} activeTask={activeTask} icons={icons} iconsInBin={iconsInBin} startingUp={startingUp} setStartingUp={setStartingUp} />

      </ThemeProvider>
    </Helmet>
  )
  };

export default App;
